import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTableNotifications } from "@/components/tables/hooks/useTableNotifications";

export interface AccessFailureLogRecord {
  id: number;
  attempt_time: string;
  username: string | null;
  locked_out: boolean;
  user_id: string | null;
  currently_locked_out: boolean | null;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
}

interface UnblockResponse {
  unblocked: boolean;
  message?: string;
}

export function useUnblockLoginAccess(onRefresh: () => void) {
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const { handleError, errorModal } = useErrorModal();
  const notifications = useTableNotifications();
  const pendingUsersRef = useRef(new Set<string>());
  const [pendingUsers, setPendingUsers] = useState(new Set<string>());

  const unblock = async (record: AccessFailureLogRecord) => {
    const userId = record.user_id;
    if (!userId || !record.currently_locked_out || pendingUsersRef.current.has(userId)) return;

    pendingUsersRef.current.add(userId);
    setPendingUsers(new Set(pendingUsersRef.current));
    try {
      const confirmed = await showConfirmationModalAsync(
        t("unblock_user_question", { username: record.username })
      );
      if (!confirmed) return;

      try {
        const response = await apiRequest<UnblockResponse>({
          entity: "user",
          entityId: userId,
          method: "put",
          additionalRouteParts: { unblock_login_access: "/" },
          submitData: { require_locked_out: true },
          shouldNotImpersonate: true,
        });
        notifications.notify(
          response.data.unblocked
            ? t("user_successfully_unblocked")
            : response.data.message ?? t("access_failure_log_no_longer_locked_out"),
          response.data.unblocked ? "success" : "info"
        );
      } catch (error) {
        handleError(error);
      } finally {
        onRefresh();
      }
    } finally {
      pendingUsersRef.current.delete(userId);
      setPendingUsers(new Set(pendingUsersRef.current));
    }
  };

  return { unblock, pendingUsers, errorModal, notifications };
}
